import React, { useState } from 'react';
import { portfolioItems } from '../data/mock';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { TrendingUp, Users, Target } from 'lucide-react';

const Portfolio = () => {
  const [selectedCategory, setSelectedCategory] = useState('All');

  const categories = ['All', ...new Set(portfolioItems.map(item => item.category))];

  const filteredItems = selectedCategory === 'All' 
    ? portfolioItems 
    : portfolioItems.filter(item => item.category === selectedCategory);

  return (
    <section id="portfolio" className="py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 
            className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4"
            style={{ fontFamily: 'var(--font-serif)' }}
          >
            Our Portfolio
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Real results for real businesses. Explore how we've helped companies achieve extraordinary growth.
          </p>
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {categories.map((category) => (
            <Button
              key={category}
              onClick={() => setSelectedCategory(category)}
              variant={selectedCategory === category ? 'default' : 'outline'}
              style={selectedCategory === category ? { backgroundColor: '#D97904' } : { borderColor: '#D97904', color: '#D97904' }}
              className={`transition-all duration-200 ${
                selectedCategory === category 
                  ? 'text-white hover:opacity-90' 
                  : 'hover:bg-orange-50'
              }`}
            >
              {category}
            </Button>
          ))}
        </div>

        {/* Portfolio Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredItems.map((item) => (
            <Card 
              key={item.id}
              className="group overflow-hidden hover:shadow-2xl transition-all duration-300 border-gray-200 hover:-translate-y-2"
            >
              <div className="relative overflow-hidden h-48">
                <img 
                  src={item.image} 
                  alt={item.title}
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                <Badge className="absolute top-4 left-4 bg-white text-gray-900">
                  {item.category}
                </Badge>
              </div>
              
              <CardHeader>
                <CardTitle className="text-xl font-bold text-gray-900">
                  {item.title}
                </CardTitle>
                <p className="text-sm font-medium text-gray-500">{item.client}</p>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <CardDescription className="text-gray-600">
                  {item.description}
                </CardDescription>
                
                <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-200">
                  {Object.entries(item.results).slice(0, 3).map(([key, value], index) => {
                    const icons = [TrendingUp, Users, Target];
                    const Icon = icons[index % icons.length];
                    return (
                      <div key={key} className="text-center">
                        <Icon className="w-5 h-5 mx-auto mb-1 text-gray-700" />
                        <div className="text-sm font-bold text-gray-900">{value}</div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Portfolio;
